import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  ArrowLeft, BarChart3, Trophy, CheckCircle, XCircle, AlertTriangle,
  Home, RefreshCw, TrendingUp, Calendar, Download, Building2, 
  DollarSign, Percent, Activity, Shield
} from "lucide-react";
import { ChatToggleButton } from "@/components/AIChatWidget";
import axios from "axios";
import { toast } from "sonner";
import { formatIrr } from "@/utils/irr";

const DealComparison = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [analysisType, setAnalysisType] = useState("rental");
  const [availableAnalyses, setAvailableAnalyses] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [comparison, setComparison] = useState(null);

  useEffect(() => {
    fetchAvailable();
  }, [analysisType, token]);

  const fetchAvailable = async () => {
    try {
      const response = await axios.get(`${API}/compare/available/${analysisType}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableAnalyses(response.data.analyses || []);
      setSelectedIds([]);
      setComparison(null);
    } catch (error) {
      toast.error("Failed to load analyses");
    }
  };

  const toggleSelection = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(i => i !== id));
    } else if (selectedIds.length < 5) {
      setSelectedIds([...selectedIds, id]);
    } else {
      toast.error("Maximum 5 analyses for comparison");
    }
  };

  const runComparison = async () => {
    if (selectedIds.length < 2) {
      toast.error("Select at least 2 analyses to compare");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/compare/deals`, {
        analysis_ids: selectedIds,
        analysis_type: analysisType
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setComparison(response.data);
      toast.success("Comparison complete!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to compare deals");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    if (value === undefined || value === null) return 'N/A';
    if (typeof value === 'string') return value;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatPercent = (value) => {
    if (value === undefined || value === null) return 'N/A';
    return `${Number(value).toFixed(1)}%`;
  };

  const formatValue = (value, key) => {
    if (value === undefined || value === null) return 'N/A';
    if (key.includes('price') || key.includes('cash') || key.includes('investment') || key.includes('profit') || key.includes('equity')) {
      return formatCurrency(value);
    }
    if (key.includes('rate') || key.includes('return') || key.includes('roi') || key.includes('irr') || key.includes('occupancy')) {
      return formatPercent(value);
    }
    if (key === 'dscr') {
      return `${Number(value).toFixed(2)}x`;
    }
    return value;
  };

  // Calculate risk level based on DSCR
  const getRiskLevel = (analysis) => {
    const dscr = analysis.results?.dscr || 0;
    if (dscr >= 1.25) return { level: 'low', label: 'Low', icon: '✅', color: 'text-emerald-500', bg: 'bg-emerald-500/20' };
    if (dscr >= 1.10) return { level: 'medium', label: 'Med', icon: '⚠️', color: 'text-amber-500', bg: 'bg-amber-500/20' };
    return { level: 'high', label: 'High', icon: '🚨', color: 'text-red-500', bg: 'bg-red-500/20' };
  };

  // Export comparison to CSV
  const exportToCSV = () => {
    if (!comparison) return;
    
    const headers = ['Metric', ...comparison.analyses.map(a => a.property_name)];
    const rows = [headers.join(',')];
    
    Object.entries(comparison.metrics_comparison).forEach(([key, metric]) => {
      const row = [
        `"${metric.label}"`,
        ...metric.values.map(v => {
          const val = v.value;
          if (typeof val === 'number') return val;
          return `"${val}"`;
        })
      ];
      rows.push(row.join(','));
    });
    
    const csv = rows.join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `deal_comparison_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    toast.success('Comparison exported to CSV!');
  };

  const TypeIcon = {
    rental: Home,
    brrrr: RefreshCw,
    flip: TrendingUp,
    airbnb: Calendar
  }[analysisType] || Home;

  return (
    <div className="min-h-screen bg-slate-900" data-testid="deal-comparison-page">
      {/* Header */}
      <header className="glass border-b border-slate-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate("/dashboard")} className="text-slate-400">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div className="flex items-center gap-2">
                <BarChart3 className="h-6 w-6 text-amber-500" />
                <h1 className="text-lg font-semibold text-white font-['Manrope']">Dealflow Manager</h1>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {comparison && (
                <Button 
                  variant="outline" 
                  onClick={exportToCSV}
                  className="text-slate-300 border-slate-700"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export CSV
                </Button>
              )}
              <Button
                onClick={runComparison}
                disabled={selectedIds.length < 2 || loading}
                className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
              >
                {loading ? "Comparing..." : `Compare (${selectedIds.length})`}
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Analysis Type Selector */}
        <div className="glass-card rounded-xl p-6 mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-white mb-1">Your Active Deals</h2>
              <p className="text-sm text-slate-400">Select properties to compare side-by-side</p>
            </div>
            <Select value={analysisType} onValueChange={setAnalysisType}>
              <SelectTrigger className="w-48 bg-slate-950 border-slate-800 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-800">
                <SelectItem value="rental">Rental Analysis</SelectItem>
                <SelectItem value="brrrr">BRRRR Analysis</SelectItem>
                <SelectItem value="flip">Flip Analysis</SelectItem>
                <SelectItem value="airbnb">Airbnb Analysis</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Card-Based Deal Selection */}
        {availableAnalyses.length === 0 ? (
          <div className="glass-card rounded-xl p-12 text-center">
            <BarChart3 className="h-16 w-16 text-slate-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No Analyses Found</h3>
            <p className="text-slate-400 mb-6">
              Create some {analysisType} analyses first to compare them here
            </p>
            <Button 
              onClick={() => navigate(`/${analysisType}-analysis`)}
              className="bg-amber-500 hover:bg-amber-600 text-slate-900"
            >
              Create {analysisType.charAt(0).toUpperCase() + analysisType.slice(1)} Analysis
            </Button>
          </div>
        ) : (
          <>
            {/* Deal Cards Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
              {availableAnalyses.map((analysis) => {
                const risk = getRiskLevel(analysis);
                const isSelected = selectedIds.includes(analysis.id);
                const irr = analysis.results?.irr ?? null;
                const dscr = analysis.results?.dscr || null;
                const cashFlow = analysis.results?.monthly_cash_flow || null;
                const coc = analysis.results?.cash_on_cash || analysis.results?.cash_on_cash_return || null;
                
                return (
                  <div
                    key={analysis.id}
                    onClick={() => toggleSelection(analysis.id)}
                    className={`relative cursor-pointer transition-all ${
                      isSelected
                        ? 'ring-2 ring-amber-500 transform scale-[1.02]'
                        : 'hover:ring-1 hover:ring-slate-600'
                    }`}
                  >
                    <div className={`glass-card rounded-xl p-4 h-full ${isSelected ? 'border-amber-500' : ''}`}>
                      {/* Selection indicator */}
                      <div className="absolute top-3 right-3">
                        <Checkbox
                          checked={isSelected}
                          className="border-slate-600 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
                        />
                      </div>
                      
                      {/* Property Name */}
                      <div className="mb-4 pr-6">
                        <div className="text-xs text-slate-500 mb-1 truncate">
                          {analysis.address || 'Property'}
                        </div>
                        <h3 className="text-white font-semibold truncate">
                          {analysis.property_name}
                        </h3>
                      </div>
                      
                      {/* Key Metrics */}
                      <div className="space-y-3">
                        {/* IRR */}
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-slate-400">IRR</span>
                          <span className="font-mono text-sm text-cyan-400 font-semibold">
                            {formatIrr(irr, 1)}
                          </span>
                        </div>
                        
                        {/* DSCR */}
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-slate-400">DSCR</span>
                          <span className={`font-mono text-sm font-semibold ${
                            dscr >= 1.25 ? 'text-emerald-400' : 
                            dscr >= 1.10 ? 'text-amber-400' : 'text-red-400'
                          }`}>
                            {dscr ? `${Number(dscr).toFixed(2)}` : 'N/A'}
                          </span>
                        </div>
                        
                        {/* Cash Flow */}
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-slate-400">Cash Flow</span>
                          <span className={`font-mono text-sm font-semibold ${
                            cashFlow >= 0 ? 'text-emerald-400' : 'text-red-400'
                          }`}>
                            {cashFlow ? formatCurrency(cashFlow) : 'N/A'}
                          </span>
                        </div>
                        
                        {/* CoC */}
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-slate-400">CoC</span>
                          <span className="font-mono text-sm text-violet-400 font-semibold">
                            {coc ? `${Number(coc).toFixed(1)}%` : 'N/A'}
                          </span>
                        </div>
                      </div>
                      
                      {/* Risk Level */}
                      <div className={`mt-4 pt-3 border-t border-slate-700 flex items-center justify-between`}>
                        <span className="text-xs text-slate-400">Risk</span>
                        <div className={`flex items-center gap-1.5 px-2 py-1 rounded ${risk.bg}`}>
                          <span>{risk.icon}</span>
                          <span className={`text-xs font-semibold ${risk.color}`}>{risk.label}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Selection Info */}
            <div className="text-center text-sm text-slate-400 mb-8">
              {selectedIds.length === 0 ? (
                'Click on deals to select them for comparison (2-5 deals)'
              ) : (
                `${selectedIds.length} deal${selectedIds.length > 1 ? 's' : ''} selected`
              )}
            </div>
          </>
        )}

        {/* Comparison Results */}
        {comparison && (
          <div className="space-y-6">
            {/* Winner Banner */}
            <div className="bg-gradient-to-r from-amber-500/20 to-amber-500/5 border border-amber-500/30 rounded-xl p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-full bg-amber-500/20">
                    <Trophy className="h-8 w-8 text-amber-500" />
                  </div>
                  <div>
                    <div className="text-sm text-amber-500 font-medium">Best Deal</div>
                    <div className="text-2xl font-bold text-white">{comparison.winner.name}</div>
                    <div className="text-sm text-slate-400">
                      Won {comparison.winner.wins} of {comparison.winner.total_metrics} metrics
                    </div>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  onClick={exportToCSV}
                  className="text-amber-500 border-amber-500/30 hover:bg-amber-500/10"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export to CSV
                </Button>
              </div>
            </div>

            {/* Side-by-Side Card Comparison */}
            <div className="glass-card rounded-xl p-6">
              <h3 className="font-semibold text-white mb-6">Side-by-Side Comparison</h3>
              
              {/* Header Row with Property Names */}
              <div className="grid gap-4 mb-6" style={{ gridTemplateColumns: `200px repeat(${comparison.analyses.length}, 1fr)` }}>
                <div className="text-sm font-semibold text-slate-400 uppercase tracking-wider">
                  Metric
                </div>
                {comparison.analyses.map((analysis) => {
                  const risk = getRiskLevel(analysis);
                  return (
                    <div key={analysis.id} className="text-center">
                      <div className="font-semibold text-white truncate mb-1">
                        {analysis.property_name}
                      </div>
                      {comparison.winner.id === analysis.id && (
                        <div className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-500/20 rounded text-amber-500 text-xs">
                          <Trophy className="h-3 w-3" />
                          Winner
                        </div>
                      )}
                      <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ml-1 ${risk.bg}`}>
                        <span>{risk.icon}</span>
                        <span className={risk.color}>{risk.label} Risk</span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Metrics Rows */}
              <div className="space-y-2">
                {Object.entries(comparison.metrics_comparison).map(([key, metric]) => (
                  <div 
                    key={key} 
                    className="grid gap-4 py-3 border-b border-slate-800/50"
                    style={{ gridTemplateColumns: `200px repeat(${comparison.analyses.length}, 1fr)` }}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-slate-300 text-sm">{metric.label}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                        metric.better === 'higher' ? 'bg-emerald-500/20 text-emerald-500' : 'bg-cyan-500/20 text-cyan-500'
                      }`}>
                        {metric.better === 'higher' ? '↑' : '↓'}
                      </span>
                    </div>
                    {metric.values.map((val) => (
                      <div key={val.id} className="text-center">
                        <span className={`font-mono text-sm ${
                          metric.winner_id === val.id 
                            ? 'text-emerald-400 font-bold' 
                            : 'text-slate-300'
                        }`}>
                          {formatValue(val.value, key)}
                        </span>
                        {metric.winner_id === val.id && (
                          <CheckCircle className="inline h-4 w-4 text-emerald-500 ml-1" />
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>

            {/* Legend */}
            <div className="flex items-center justify-center gap-6 text-sm text-slate-400">
              <div className="flex items-center gap-2">
                <span className="bg-emerald-500/20 text-emerald-500 px-2 py-0.5 rounded text-xs">↑</span>
                <span>Higher is better</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="bg-cyan-500/20 text-cyan-500 px-2 py-0.5 rounded text-xs">↓</span>
                <span>Lower is better</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="bg-emerald-500/20 text-emerald-500 px-2 py-0.5 rounded text-xs">✅ Low</div>
                <div className="bg-amber-500/20 text-amber-500 px-2 py-0.5 rounded text-xs">⚠️ Med</div>
                <div className="bg-red-500/20 text-red-500 px-2 py-0.5 rounded text-xs">🚨 High</div>
                <span>Risk Level</span>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default DealComparison;
